import {useMemo, useState} from "react";
import {PhoneHQInput, PhoneHQGlyph, PhoneHQLoader, PhoneHQAvatar, PhoneHQButton} from 'ch3-ui-lib';
import {useSelector} from "react-redux";
import {selectUserId, selectUserRole} from "../../../store/features/Auth/authSlice";
import {useListEnsUserContactsQuery} from "../../../store/features/ens/ensService";
import {useFetchGroupsAdminQuery, useGroupsForFiltersQuery} from "../../../store/features/groups/groupsService";
import {EnsTtsRequest, EnsUserContactListItem, EnsVoicePrompt} from "../../../store/features/ens/types";
import {EnsTtsPromptInput} from "../components/EnsTtsPromptInput";
import {EnsTextareaField} from "../components/EnsTextareaField";
import {SelectableMember, SelectMembersDialog} from "../../../components/SelectMembersDialog/SelectMembersDialog";
import {getFullName} from "../../../utilis/getFullName";
import {
    formatDirectRecipientsWithoutNumber,
    formatTargetSummary,
    hasEligibleEnsNumber,
} from "./utils";

const contactToMember = (contact: EnsUserContactListItem): SelectableMember<EnsUserContactListItem> => ({
    id: contact.userId,
    name: `${contact.firstName} ${contact.lastName}`.trim() || contact.email,
    subtitle: hasEligibleEnsNumber(contact) ? contact.email : `${contact.email} · no emergency number`,
    avatarUrl: contact.avatarUrl,
    data: contact,
});

const GROUP_PICKER_LIMIT = 1000;
const USER_PICKER_LIMIT = 50;
const MESSAGE_MAX = 500;

interface Props {
    name: string;
    message: string;
    targetGroupIds: number[];
    targetUsers: EnsUserContactListItem[];
    voicePrompt: EnsVoicePrompt | null;
    voicePromptDraft: EnsTtsRequest;
    autoPreviewVoicePrompt?: boolean;
    voicePromptRequired?: boolean;
    showVoicePrompt?: boolean;
    externalNumberRequired: boolean;
    onChange: (patch: Partial<{
        name: string;
        message: string;
        targetGroupIds: number[];
        targetUsers: EnsUserContactListItem[];
    }>) => void;
    onVoicePromptChange: (value: EnsVoicePrompt | null) => void;
    onVoicePromptDraftChange: (value: EnsTtsRequest) => void;
}

export const Step2Target = ({
    name,
    message,
    targetGroupIds,
    targetUsers,
    voicePrompt,
    voicePromptDraft,
    autoPreviewVoicePrompt = false,
    voicePromptRequired = true,
    showVoicePrompt = true,
    externalNumberRequired,
    onChange,
    onVoicePromptChange,
    onVoicePromptDraftChange,
}: Props) => {
    const userId = useSelector(selectUserId);
    const role = useSelector(selectUserRole);
    const [userPhrase, setUserPhrase] = useState('');
    const [showManageMembers, setShowManageMembers] = useState(false);
    const isAdmin = role === 'ADMIN' || role === 'SUPERADMIN';

    const {data: userGroups, isLoading: isUserGroupsLoading} = useGroupsForFiltersQuery(
        {userId},
        {skip: isAdmin}
    );
    const {data: adminGroups, isLoading: isAdminGroupsLoading} = useFetchGroupsAdminQuery(
        {userId, phrase: '', limit: GROUP_PICKER_LIMIT},
        {skip: !isAdmin}
    );
    const {data: contactsPage, isFetching: isContactsFetching} = useListEnsUserContactsQuery({
        offset: 0,
        limit: USER_PICKER_LIMIT,
        phrase: userPhrase.trim() || undefined,
    }, {skip: !isAdmin});

    const groups = useMemo(() => {
        if (isAdmin) {
            return adminGroups?.groups.map(({group, users}) => ({
                ...group,
                memberCount: users?.length,
            })) ?? [];
        }
        return (userGroups ?? []).map((group) => ({
            ...group,
            memberCount: undefined as number | undefined,
        }));
    }, [adminGroups, isAdmin, userGroups]);

    const isLoading = isAdmin ? isAdminGroupsLoading : isUserGroupsLoading;
    const selectedUserIds = useMemo(() => new Set(targetUsers.map((user) => user.userId)), [targetUsers]);
    const contactMembers = useMemo(
        () => (contactsPage?.data ?? []).map(contactToMember),
        [contactsPage?.data],
    );
    const selectedMembers = useMemo(() => targetUsers.map(contactToMember), [targetUsers]);

    const toggleGroup = (groupId: number) => {
        if (targetGroupIds.includes(groupId)) {
            onChange({targetGroupIds: targetGroupIds.filter((id) => id !== groupId)});
        } else {
            onChange({targetGroupIds: [...targetGroupIds, groupId]});
        }
    };

    const toggleUser = (contact: EnsUserContactListItem) => {
        if (selectedUserIds.has(contact.userId)) {
            onChange({targetUsers: targetUsers.filter((user) => user.userId !== contact.userId)});
        } else {
            onChange({targetUsers: [...targetUsers, contact]});
        }
    };

    const targetCount = targetGroupIds.length + targetUsers.length;
    const usersWithoutNumber = useMemo(
        () => targetUsers.filter((user) => !hasEligibleEnsNumber(user)),
        [targetUsers],
    );
    const memberName = (user: EnsUserContactListItem) => getFullName(user.firstName, user.lastName) || user.email;
    const usersWithoutNumberNames = usersWithoutNumber.map(memberName).join(', ');

    const renderGroupChip = (group: (typeof groups)[number]) => {
        const selected = targetGroupIds.includes(group.groupId);
        return (
            <button
                key={group.groupId}
                type="button"
                aria-pressed={selected}
                onClick={() => toggleGroup(group.groupId)}
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors ${
                    selected
                        ? 'border-brand bg-brand-tint text-brand-ink'
                        : 'border-line bg-surface text-zinc-900 hover:border-brand-focus'
                }`}
            >
                <span className="flex min-w-0 items-center gap-1">
                    <span className="max-w-[160px] truncate">{group.name}</span>
                    {group.memberCount != null && (
                        <span className="text-ink-muted">({group.memberCount})</span>
                    )}
                </span>
            </button>
        );
    };

    const renderUserChip = (user: EnsUserContactListItem) => {
        const fullName = getFullName(user.firstName, user.lastName) || user.email;
        return (
            <span key={user.userId} className="inline-flex items-center gap-2 bg-zinc-100 pl-1 pr-3 py-1 rounded-xl">
                <PhoneHQAvatar
                    size="20px"
                    type={user.avatarUrl ? "image" : "initials"}
                    src={user.avatarUrl ?? undefined}
                    name={fullName}
                    className={'border'}
                />
                <span className="text-[13px] font-medium text-zinc-900">{fullName}</span>
                {!hasEligibleEnsNumber(user) && (
                    <span
                        title="No eligible emergency phone number"
                        className="flex h-4 w-4 items-center justify-center text-amber-600"
                    >
                        <PhoneHQGlyph name="phone_disabled" size={13}/>
                    </span>
                )}
                <button
                    type="button"
                    onClick={() => toggleUser(user)}
                    className="flex h-4 w-4 items-center justify-center rounded-full bg-black/5 text-ink-muted transition-colors hover:bg-black/10"
                >
                    <PhoneHQGlyph name="close" size={12}/>
                </button>
            </span>
        );
    };

    return (
        <div className="flex flex-col gap-4">
            <div>
                <label className="mb-2 block text-sm font-semibold text-ink">Target group</label>
                {isLoading && (
                    <div className="flex items-center justify-center py-6">
                        <PhoneHQLoader/>
                    </div>
                )}
                {!isLoading && groups.length === 0 && (
                    <div className="rounded-lg border border-line bg-zinc-50 px-4 py-5 text-center text-sm text-ink-muted">
                        {isAdmin ? 'No groups available yet.' : "You're not a member of any groups."}
                    </div>
                )}
                {!isLoading && groups.length > 0 && (
                    <div className="flex max-h-32 flex-wrap gap-2 overflow-y-auto py-2 px-1">
                        {groups.map(renderGroupChip)}
                    </div>
                )}
            </div>

            <div>
                <div className="mb-2 flex items-center justify-between">
                    <label className="block text-sm font-semibold text-ink">Add individuals</label>
                    {targetUsers.length > 0 && (
                        <span className="text-xs font-semibold text-brand-ink">{targetUsers.length} added</span>
                    )}
                </div>
                {targetUsers.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                        {targetUsers.map(renderUserChip)}
                    </div>
                )}
                <PhoneHQButton
                    phq-id="ensWizard-manageIndividualsBtn"
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowManageMembers(true)}
                    className="!h-9 !rounded-lg !border-line !px-3.5 !text-brand-ink hover:!border-brand-focus"
                >
                    <PhoneHQGlyph name="group_add" size={18}/>
                    {targetUsers.length > 0 ? 'Manage individuals' : 'Add individuals…'}
                </PhoneHQButton>
            </div>

            {targetCount > 0 && (
                <div className="rounded-lg border border-line bg-zinc-50 px-3 py-2 text-sm text-ink-secondary">
                    Alert target includes <span className="font-semibold">{formatTargetSummary(targetGroupIds.length, targetUsers.length)}</span>.
                    L1 push notification starts immediately.
                </div>
            )}

            <div className="border-t border-line pt-4">
                <PhoneHQInput
                    phq-id="ensWizard-nameInput"
                    label="Alert name"
                    placeholder="Short, identifiable name"
                    value={name}
                    onChange={({target: {value: v}}) => onChange({name: v})}
                />
            </div>

            <EnsTextareaField
                phqId="ensWizard-messageInput"
                label="Alert message"
                placeholder="What recipients will see and need to confirm"
                value={message}
                maxLength={MESSAGE_MAX}
                onChange={(next) => onChange({message: next})}
            />

            {showVoicePrompt && (
                <div className="border-t border-line pt-4">
                    <div className="text-sm font-semibold text-ink">
                        Voice prompt
                        {!voicePromptRequired && <span className="ml-1 font-normal text-ink-muted">(optional)</span>}
                    </div>
                    <div className="mt-0.5 text-xs leading-5 text-ink-muted">
                        {voicePromptRequired
                            ? 'Required for this alert and played if escalation reaches an internal or phone call. We will generate it automatically when you continue; previewing is optional.'
                            : 'Save a reusable prompt with the template, or generate one later when creating an alert.'}
                    </div>
                    <div className="pt-2.5">
                        <EnsTtsPromptInput
                            value={voicePrompt}
                            draft={voicePromptDraft}
                            autoPreview={autoPreviewVoicePrompt}
                            onChange={onVoicePromptChange}
                            onDraftChange={onVoicePromptDraftChange}
                        />
                    </div>
                </div>
            )}

            {externalNumberRequired && usersWithoutNumber.length > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm leading-5 text-amber-800">
                    <PhoneHQGlyph name="phone_disabled" size={16} className="mt-0.5"/>
                    <div>
                        <div className="font-semibold">
                            {formatDirectRecipientsWithoutNumber(usersWithoutNumber.length, targetUsers.length)}
                        </div>
                        <div className="mt-1">
                            Affected: <span className="font-semibold">{usersWithoutNumberNames}</span>.
                        </div>
                        <div className="mt-1">
                            Add their emergency numbers, remove the affected recipients, or remove SMS and phone-call
                            steps before sending.
                        </div>
                        {targetGroupIds.length > 0 && (
                            <div className="mt-1">
                                Group members are not included in this count. They will be expanded and validated when
                                the alert is sent.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showManageMembers && (
                <SelectMembersDialog
                    title="Add individuals"
                    confirmLabel="Add individuals"
                    members={contactMembers}
                    initialSelected={selectedMembers}
                    loading={isContactsFetching}
                    searchValue={userPhrase}
                    onSearchChange={setUserPhrase}
                    searchPlaceholder="Search users"
                    emptyLabel="No users found."
                    onConfirm={(selected) => onChange({targetUsers: selected.map((m) => m.data)})}
                    onClose={() => setShowManageMembers(false)}
                />
            )}
        </div>
    );
};
